import {
  Calendar,
  ChevronDown,
  Download,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  TriangleAlert,
  Voicemail,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { CallRow, Inbox } from '../../api/types';
import { identityLine, isManagedInbox, needsReauth } from '../../lib/channelMeta';
import { RefreshButton } from '../../components/RefreshButton';
import { callDuration } from '../../lib/commsFormat';
import { useTimeFormat } from '../../lib/tenantTime';
import { useDismissOnOutside } from '../../lib/useDismiss';
import { useLoad } from '../../lib/useLoad';
import { CallMenuRow } from './CallButton';
import { buildCallOptions, type CallOption, RC_DIAL_FAILED } from './callOptions';
import { ShowChannelBar } from './channelBar';
import { useComms } from './context';
import { useConfigNav } from './useConfigNav';
import { useModuleRefresh } from './useModuleRefresh';
import { useDialer } from './dialer';
import { buildNewCallRoutes, type NewCallRoute } from './newCallRoutes';
import { usePhone } from './phone';
import { TableRowsSkeleton } from './Skeletons';
import type { RcDialer } from './useRcDialer';

type CallFilter = 'all' | 'inbound' | 'outbound' | 'missed' | 'voicemail';

type RangeKey = '7d' | '30d' | 'all';

const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: 'all', label: 'All time', days: null },
];

/**
 * Call this row's number back. A table row gets ONE round button rather than
 * the thread header's split control — the route it would take is in the title,
 * and a menu per row in a 500-row table is noise. It picks the same way the
 * header does: the first route that can actually run.
 */
function CallBackButton({
  number,
  routes,
}: {
  number: string;
  routes: (to: string) => CallOption[];
}) {
  if (!number) return null;
  const options = routes(number);
  const primary = options.find((option) => !option.blocked) ?? options[0] ?? null;
  if (!primary) return null;
  return (
    <button
      type="button"
      className="calls-round-btn"
      disabled={Boolean(primary.blocked)}
      title={primary.blocked ?? `Call ${number} from ${primary.from} · ${primary.hint}`}
      onClick={primary.run}
    >
      <Phone size={12} />
    </button>
  );
}

/**
 * The floating New call control: one button that opens the one phone, or the
 * route picker when the agent has both. Bottom-left on purpose — see the CSS.
 */
function NewCallButton({ routes }: { routes: NewCallRoute[] }) {
  const [open, setOpen] = useState(false);
  const menuRef = useDismissOnOutside<HTMLDivElement>(open, () => setOpen(false));
  if (routes.length === 0) return null;

  if (routes.length === 1) {
    const [route] = routes;
    return (
      <div className="calls-fab">
        <button
          type="button"
          className="calls-fab-btn"
          disabled={Boolean(route.blocked)}
          title={route.blocked ?? `${route.label} · ${route.hint}`}
          onClick={route.open}
        >
          <Phone size={15} />
          New call · {route.label}
        </button>
      </div>
    );
  }

  return (
    <div className="calls-fab">
      <button
        type="button"
        className="calls-fab-btn"
        title="Start a call from a keypad"
        onClick={() => setOpen((current) => !current)}
      >
        <Phone size={15} />
        New call
        <ChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
      </button>
      {open && (
        <div className="thr-menu thr-call-menu" ref={menuRef}>
          {routes.map((route) => (
            <CallMenuRow
              key={route.key}
              label={route.label}
              about={route.hint.charAt(0).toUpperCase() + route.hint.slice(1)}
              blocked={route.blocked}
              onPick={() => {
                setOpen(false);
                route.open();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The RingCentral dialer holds its own sign-in, separate from the CRM's, and a
 * dialer nobody has signed into cannot ring — so an agent who expects
 * RingCentral calls to reach this browser and never signed in is quietly
 * missing every one. This says so, once the widget has actually reported
 * (`known`): it announces "not signed in" while it boots, before restoring the
 * session it remembers, and a bar that flashed on every load is one people
 * learn to ignore.
 *
 * Only the signed-OUT state is called out. Signed in but not registered is
 * ambiguous — with the dashboard open in another tab, RingCentral connects the
 * web phone in ONE of them and the rest relay to it, which is healthy — and a
 * warning about a working setup would teach the same lesson.
 */
function DialerSignInBar({ dialer }: { dialer: RcDialer }) {
  if (!dialer.available || !dialer.known || dialer.loggedIn) return null;
  return (
    <div className="calls-signin" role="status">
      <TriangleAlert size={15} />
      <span className="calls-signin-text">
        <b>The RingCentral dialer isn't signed in.</b> Until it is, RingCentral calls ring only your
        desk phone or the RingCentral app — not this browser.
      </span>
      <button type="button" className="cbtn" onClick={dialer.openPanel}>
        Open dialer
      </button>
    </div>
  );
}

/**
 * The phone lines that have stopped answering to us.
 *
 * A channel whose authorization lapsed still has every call it already took in
 * the log below, so nothing on this screen looks wrong — inbound simply stops
 * arriving and RingOut stops placing, both silently. That silence is the whole
 * reason this bar exists, and it sits beside the dialer's own sign-in bar
 * because the two answer the same question ("why is this phone quiet?") from
 * opposite ends.
 *
 * Only voice-carrying channels count here: a Twilio SMS sender that needs
 * reconnecting is a real fault and belongs in the inbox rail, not on a screen
 * about calls.
 */
function CallChannelsStaleBar({ inboxes }: { inboxes: Inbox[] }) {
  const openConfig = useConfigNav();
  const stale = useMemo(
    () =>
      inboxes.filter(
        (inbox) =>
          needsReauth(inbox) &&
          (inbox.channelType === 'ringcentral' ||
            (inbox.channelType === 'twilio' && inbox.channel.voiceEnabled === true)),
      ),
    [inboxes],
  );
  if (stale.length === 0) return null;
  const fixable = stale.filter(isManagedInbox);
  return (
    <div className="calls-signin" role="status">
      <TriangleAlert size={15} />
      <span className="calls-signin-text">
        {stale.length === 1 ? (
          <>
            <b>{identityLine(stale[0])} needs reconnecting.</b> It lost its authorization — calls on
            that number are not arriving and cannot be placed from here until it is signed in again.
          </>
        ) : (
          <>
            <b>{stale.length} phone lines need reconnecting.</b> {stale.map(identityLine).join(', ')} —
            calls on them are not arriving and cannot be placed from here until they are signed in
            again.
          </>
        )}
      </span>
      {fixable.length > 0 && (
        <button
          type="button"
          className="cbtn"
          onClick={() => openConfig(fixable.length === 1 ? `/inboxes/${fixable[0].id}` : '/')}
        >
          Reconnect
        </button>
      )}
    </div>
  );
}

/** "3:47" for the stat cards (the design drops the leading zero there). */
const statDuration = (seconds: number) => callDuration(seconds).replace(/^0/, '');

function exportCsv(rows: CallRow[]): void {
  const esc = (value: string | number | null) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const lines = [
    'direction,contact,number,started_at,duration_seconds,disposition,recording',
    ...rows.map((row) =>
      [
        row.direction,
        esc(row.contactName),
        esc(row.peerNumber),
        row.startedAt,
        row.durationSeconds ?? '',
        esc(row.disposition),
        row.hasRecording ? 'yes' : 'no',
      ].join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'call-log.csv';
  link.click();
  URL.revokeObjectURL(url);
}

const FILTERS: { key: CallFilter; label: string }[] = [
  { key: 'all', label: 'All calls' },
  { key: 'inbound', label: 'Inbound' },
  { key: 'outbound', label: 'Outbound' },
  { key: 'missed', label: 'Missed' },
  { key: 'voicemail', label: 'Voicemail' },
];

const DIRECTION_META: Record<
  CallRow['direction'],
  { label: string; icon: typeof PhoneIncoming; bg: string; fg: string }
> = {
  inbound: { label: 'Inbound', icon: PhoneIncoming, bg: 'var(--c-green-muted)', fg: 'var(--c-green)' },
  outbound: { label: 'Outbound', icon: PhoneOutgoing, bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)' },
  missed: { label: 'Missed', icon: PhoneIncoming, bg: 'var(--c-red-muted)', fg: 'var(--c-red)' },
  voicemail: { label: 'Voicemail', icon: Voicemail, bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)' },
};

/**
 * Calls & recordings: the call log, full width, and one floating control to
 * start a call. The phones themselves are panels — the RingCentral dialer and
 * the Twilio keypad — that float in over any tab, the same way they do in the
 * dispatch dashboard, so this tab no longer carries a dialer column of its own.
 * The history table binds to GET /calls.
 */
export function CallsView({ onOpenConversation }: { onOpenConversation: (id: number) => void }) {
  const { messageTime } = useTimeFormat();
  const { source, inboxes, canCall, capabilities } = useComms();
  const phone = usePhone();
  const rcDialer = useDialer();
  const [filter, setFilter] = useState<CallFilter>('all');
  const [range, setRange] = useState<RangeKey>('7d');
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useDismissOnOutside<HTMLDivElement>(rangeOpen, () => setRangeOpen(false));
  const [callError, setCallError] = useState<string | null>(null);

  /**
   * Calling a row back. Same routes as a conversation's Call button, but a log
   * row has no contact to hang the call on — RingOut goes to the number
   * endpoint instead (see callCont.ringout).
   */
  const callRoutesFor = (to: string) =>
    buildCallOptions({
      to,
      // A log row belongs to no thread, so nothing leads on that account: the
      // order is simply the softphone's own inbox first, then RingCentral.
      threadInboxId: phone?.inbox?.id ?? null,
      inboxes,
      canCall,
      phone,
      rcDialer,
      agentPhone: capabilities?.phone ?? null,
      agentPhoneKnown: capabilities !== null && capabilities.phone !== undefined,
      dial: (number) => {
        setCallError(null);
        void phone?.call(number);
      },
      ringOut: (inboxId) => {
        setCallError(null);
        source.api.ringoutNumber({ inboxId, to }).catch((error: unknown) => {
          setCallError(error instanceof Error ? error.message : 'That call did not go through.');
        });
      },
      rcDial: (number) => {
        setCallError(null);
        void rcDialer?.dial(number).then((started) => {
          if (!started) setCallError(RC_DIAL_FAILED);
        });
      },
    });
  const log = useLoad(
    () =>
      source.api
        .listCalls()
        .then((result) => ({ ...result, fetchedAt: Date.now() }))
        .catch((error: unknown) => {
          // ONLY a service that has no /calls endpoint. Swallowing everything
          // reported a 502, or a dropped connection, to the agent as "call
          // history is not deployed yet" — a false diagnosis of a temporary
          // failure, under an empty table that looked permanent.
          if (error instanceof ApiError && error.kind === 'notFound') {
            return { stats: null, payload: [] as CallRow[], fetchedAt: Date.now() };
          }
          throw error;
        }),
    [source],
  );

  useModuleRefresh(log.reload);

  /**
   * The log re-reads itself as calls happen. It was one snapshot at mount: an
   * agent parked here through three calls saw none of them, a row marked live
   * stayed lit after its call had ended, and the tiles counted the day as it
   * stood when the screen opened. The same `call.*` events the ring card is
   * drawn from say when the picture changed, and a reconnect says the socket
   * was not to be trusted for a while. One request per event on a screen that
   * is looking at exactly that; out-of-order answers are dropped by the load's
   * ticket.
   */
  const reloadLog = log.reload;
  useEffect(
    () =>
      source.subscribe((event) => {
        if (event.name.startsWith('call.') || event.name === 'reconnected') reloadLog();
      }),
    [source, reloadLog],
  );

  const inRange = useMemo(() => {
    const all = log.data?.payload ?? [];
    const days = RANGES.find((r) => r.key === range)?.days ?? null;
    if (days === null) return all;
    const cutoff = (log.data?.fetchedAt ?? 0) - days * 86_400_000;
    return all.filter((row) => new Date(row.startedAt).getTime() >= cutoff);
  }, [log.data, range]);

  const rows = useMemo(
    () => (filter === 'all' ? inRange : inRange.filter((row) => row.direction === filter)),
    [inRange, filter],
  );

  const stats = log.data?.stats ?? null;
  const apiMissing = !log.loading && log.data !== null && log.data.stats === null;
  const newCallRoutes = buildNewCallRoutes({ dialer: rcDialer, phone });

  return (
    <div className="calls">
      <div className="calls-main">
        <CallChannelsStaleBar inboxes={inboxes} />
        {rcDialer && <DialerSignInBar dialer={rcDialer} />}
        {callError && <div className="comms-error-note">{callError}</div>}
        <div className="calls-filterbar">
          {/* No rail on this tab either, and the dashboard's Call Logs shortcut
              opens it with the channel bar folded. */}
          <ShowChannelBar />
          {FILTERS.map(({ key, label }) => {
            const count =
              key === 'all'
                ? inRange.length
                : inRange.filter((row) => row.direction === key).length;
            return (
              <button
                key={key}
                type="button"
                className={`calls-filter${filter === key ? ' on' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
                {count > 0 && <span className="calls-filter-count">{count}</span>}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <div style={{ position: 'relative' }}>
            <button type="button" className="cbtn" onClick={() => setRangeOpen(!rangeOpen)}>
              <Calendar size={13} />
              {RANGES.find((r) => r.key === range)?.label}
              <ChevronDown size={11} />
            </button>
            {rangeOpen && (
              <div className="thr-menu" ref={rangeRef} style={{ top: 32, right: 0 }}>
                {RANGES.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setRange(key);
                      setRangeOpen(false);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <RefreshButton
            className="cbtn"
            size={13}
            title="Refresh the call log"
            refreshing={log.refreshing}
            busy={log.loading}
            onClick={log.reload}
          />
          <button
            type="button"
            className="cbtn"
            disabled={rows.length === 0}
            title={rows.length === 0 ? 'Nothing to export yet' : 'Download the visible rows as CSV'}
            onClick={() => exportCsv(rows)}
          >
            <Download size={13} />
            Export
          </button>
        </div>

        <div className="cstat-row">
          <div className="cstat">
            <div className="cstat-label">Answered today</div>
            <div className="cstat-value">{stats ? stats.answeredToday : '—'}</div>
          </div>
          <div className="cstat">
            <div className="cstat-label">Missed</div>
            <div className={`cstat-value${stats && stats.missedToday > 0 ? ' red' : ''}`}>
              {stats ? stats.missedToday : '—'}
            </div>
          </div>
          <div className="cstat">
            <div className="cstat-label">Avg handle time</div>
            <div className="cstat-value">
              {stats?.avgDurationSeconds != null ? statDuration(stats.avgDurationSeconds) : '—'}
            </div>
          </div>
          <div className="cstat">
            <div className="cstat-label">Voicemails unheard</div>
            <div className={`cstat-value${stats && stats.voicemailsUnheard > 0 ? ' orange' : ''}`}>
              {stats ? stats.voicemailsUnheard : '—'}
            </div>
          </div>
        </div>

        <div className="ctable">
          <div className="ctable-head">
            <div style={{ width: 104, flex: '0 0 104px' }}>DIRECTION</div>
            <div style={{ flex: 1.5 }}>CONTACT</div>
            <div style={{ flex: 1 }}>NUMBER</div>
            <div style={{ width: 130, flex: '0 0 130px' }}>WHEN</div>
            <div style={{ width: 66, flex: '0 0 66px' }}>LENGTH</div>
            <div style={{ flex: 1.4 }}>DISPOSITION</div>
            <div style={{ width: 170, flex: '0 0 170px', textAlign: 'right' }}>ACTIONS</div>
          </div>
          {log.loading && log.data === null && (
            <TableRowsSkeleton columns={[1, 1.5, 1, 1.3, 0.7, 1.4, 1.7]} count={7} />
          )}
          {rows.map((row, index) => {
            const direction = DIRECTION_META[row.direction];
            const DirIcon = direction.icon;
            return (
              <div
                key={row.id}
                className={`ctable-row${index % 2 === 1 ? ' odd' : ''}${row.live ? ' live' : ''}`}
              >
                <div style={{ width: 104, flex: '0 0 104px' }}>
                  <span
                    className="calls-dir-chip"
                    style={{ background: direction.bg, color: direction.fg }}
                  >
                    <DirIcon size={11} />
                    {direction.label}
                  </span>
                </div>
                <div style={{ flex: 1.5, minWidth: 0 }}>
                  <div className="ctable-cell-main">{row.contactName ?? 'Unknown'}</div>
                  <div className="ctable-cell-sub">{row.contactRole ?? '—'}</div>
                </div>
                <div className="ctable-cell-num" style={{ flex: 1 }}>
                  {row.peerNumber}
                </div>
                <div className="ctable-cell-muted" style={{ width: 130, flex: '0 0 130px' }}>
                  {messageTime(row.startedAt)}
                </div>
                <div className="ctable-cell-num" style={{ width: 66, flex: '0 0 66px' }}>
                  {row.durationSeconds != null ? callDuration(row.durationSeconds) : '—'}
                </div>
                <div className="ctable-cell-muted" style={{ flex: 1.4 }}>
                  {row.disposition ?? '—'}
                </div>
                <div
                  style={{
                    width: 170,
                    flex: '0 0 170px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: row.hasRecording ? 'var(--c-primary)' : 'var(--c-muted)',
                    }}
                  >
                    {row.hasRecording ? 'Recording' : 'No recording'}
                  </span>
                  {row.recordingUrl && (
                    <>
                      <a
                        className="calls-round-btn"
                        href={row.recordingUrl}
                        target="_blank"
                        rel="noreferrer"
                        title="Play recording"
                      >
                        <Play size={12} />
                      </a>
                      <a
                        className="calls-round-btn"
                        href={row.recordingUrl}
                        download
                        title="Download recording"
                      >
                        <Download size={12} />
                      </a>
                    </>
                  )}
                  {row.conversationDisplayId !== null && (
                    <button
                      type="button"
                      className="calls-round-btn"
                      title="Open conversation"
                      onClick={() =>
                        row.conversationDisplayId !== null &&
                        onOpenConversation(row.conversationDisplayId)
                      }
                    >
                      <MessageSquare size={12} />
                    </button>
                  )}
                  <CallBackButton number={row.peerNumber} routes={callRoutesFor} />
                </div>
              </div>
            );
          })}
          {rows.length === 0 && !(log.loading && log.data === null) && (
            <div className="ctable-empty">
              {log.error ? (
                <>
                  {log.error.message}
                  <br />
                  <button type="button" className="cbtn cbtn-sm" onClick={log.reload}>
                    Try again
                  </button>
                </>
              ) : apiMissing ? (
                <>
                  Call history is coming — it needs the CRM's <b>/calls</b> endpoint, which is not
                  deployed yet.
                  <br />
                  Calls already appear inside each contact's conversation thread, and New call works
                  today.
                </>
              ) : (
                'No calls match this filter yet.'
              )}
            </div>
          )}
        </div>
      </div>

      <NewCallButton routes={newCallRoutes} />
    </div>
  );
}
